import { Component, Input, forwardRef, OnInit, OnDestroy, AfterViewInit, Injector, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor, AbstractControl, ValidationErrors, NgControl, Validator, NG_VALIDATORS } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { FieldRelationshipService } from '../../services/field-relationships/field-relationship.service';

export interface DateRangeValue {
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-form-date-range',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section">
      <label [class.required-field]="required">{{ label }}</label>

      <!-- 日期限制說明 -->
      <div *ngIf="hint || (minDate && maxDate)" class="date-constraint-info">
        <small>
          <i class="fas fa-info-circle"></i>
          <span *ngIf="hint">{{ hint }}</span>
          <span *ngIf="!hint && minDate && maxDate">日期選擇已限制在 {{ minDate }} 至 {{ maxDate }} 之間</span>
        </small>
      </div>

      <!-- 只讀模式顯示 -->
      <div *ngIf="readonly" class="readonly-value">
        <div class="date-range-display">
          <span class="start-date">
            <strong>{{ startLabel || ('form.date_range.start_label' | translate) }}:</strong>
            {{ formatDateForDisplay(value.startDate) }}
          </span>
          <span class="separator"> ~ </span>
          <span class="end-date">
            <strong>{{ endLabel || ('form.date_range.end_label' | translate) }}:</strong>
            {{ formatDateForDisplay(value.endDate) }}
          </span>
        </div>
      </div>

      <!-- 編輯模式 -->
      <div *ngIf="!readonly" class="date-range-inputs">
        <div class="date-input-group">
          <label class="input-label">{{ startLabel || ('form.date_range.start_label' | translate) }}</label>
          <input
            [type]="inputType"
            [(ngModel)]="startDateValue"
            (ngModelChange)="onStartDateChange($event)"
            (blur)="onTouched()"
            [placeholder]="placeholder || ('form.date_range.start_placeholder' | translate)"
            [min]="minDate"
            [max]="maxDate"
            class="form-control start-date-input"
          />
        </div>

        <div class="date-input-group">
          <label class="input-label">{{ endLabel || ('form.date_range.end_label' | translate) }}</label>
          <input
            [type]="inputType"
            [(ngModel)]="endDateValue"
            (ngModelChange)="onEndDateChange($event)"
            (blur)="onTouched()"
            [placeholder]="placeholder || ('form.date_range.end_placeholder' | translate)"
            [min]="minEndDate"
            [max]="maxDate"
            class="form-control end-date-input"
          />
        </div>
      </div>

      <!-- 錯誤訊息顯示 -->
      <div *ngIf="errorMessage" class="error-message">
        {{ errorMessage | translate }}
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDateRangeComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormDateRangeComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss'],
  styles: [`
    .date-constraint-info {
      padding: 8px 12px;
      margin: 8px 0;
      background-color: #d1ecf1;
      border: 1px solid #bee5eb;
      border-radius: 4px;
    }

    .date-constraint-info small {
      display: block;
      line-height: 1.5;
      color: #0c5460;
      font-size: 0.85em;
    }

    .date-constraint-info i {
      margin-right: 6px;
      color: #17a2b8;
    }

    .date-range-inputs {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .date-input-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .input-label {
      font-size: 0.9em;
      font-weight: 500;
      color: #484949;
      margin-bottom: 2px;
    }

    .form-control {
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      transition: border-color 0.2s;
      color: #333;
    }

    /* 改善空值時的顯示 */
    .form-control:invalid {
      color: #999;
    }

    .form-control:focus {
      outline: none;
      border-color: #f5820d;
      box-shadow: 0 0 0 2px rgba(245, 130, 13, 0.1);
    }

    .date-range-display {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 8px 12px;
      background-color: #f5f5f5;
      border-radius: 4px;
      border: 1px solid #ddd;
    }

    .separator {
      color: #888;
      font-weight: bold;
      text-align: center;
      margin: 4px 0;
    }

    .error-message {
      color: #d9534f;
      font-size: 0.85em;
      margin-top: 4px;
      padding: 4px 8px;
      background-color: rgba(217, 83, 79, 0.1);
      border-radius: 3px;
    }

    @media (min-width: 768px) {
      .date-range-inputs {
        flex-direction: row;
        gap: 16px;
      }

      .date-input-group {
        flex: 1;
      }

      .date-range-display {
        flex-direction: row;
        align-items: center;
        gap: 16px;
      }

      .separator {
        margin: 0 8px;
      }
    }
  `]
})
export class FormDateRangeComponent implements ControlValueAccessor, Validator, OnInit, AfterViewInit, OnDestroy {
  @Input() label: string = '日期範圍';
  @Input() startLabel: string = '開始日期';
  @Input() endLabel: string = '結束日期';
  @Input() dateType: 'date' | 'datetime' = 'date';
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() placeholder: string = '';
  @Input() allowSameDate: boolean = true;
  @Input() name: string = ''; // 欄位名稱，用於從設定檔獲取約束
  @Input() hint?: string; // 日期限制提示訊息
  private _minDate?: string;
  private _maxDate?: string;

  @Input()
  set minDate(value: string | undefined) {
    this._minDate = this.formatDateForInputType(value);
    console.log('[FormDateRange] minDate setter - 輸入值:', value, '轉換後:', this._minDate);
  }
  get minDate(): string | undefined {
    return this._minDate;
  }

  @Input()
  set maxDate(value: string | undefined) {
    this._maxDate = this.formatDateForInputType(value);
    console.log('[FormDateRange] maxDate setter - 輸入值:', value, '轉換後:', this._maxDate);
  }
  get maxDate(): string | undefined {
    return this._maxDate;
  }

  value: DateRangeValue = { startDate: '', endDate: '' };
  startDateValue: string = '';
  endDateValue: string = '';
  errorMessage: string = '';

  /**
   * 結束日期的最小值
   * 規則：結束日期不能早於開始日期
   */
  get minEndDate(): string | undefined {
    // 優先：如果有開始日期，結束日期最小值就是開始日期
    if (this.startDateValue) {
      return this.startDateValue;
    }

    // 次要：如果沒有開始日期，使用 minDate
    return this.minDate;
  }

  onChange: (value: DateRangeValue | null) => void = () => {};
  onTouched: () => void = () => {};

  private ngControl: NgControl | null = null;

  constructor(
    private injector: Injector,
    private fieldRelationshipService: FieldRelationshipService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // 取得 NgControl 來存取 FormControl
    this.ngControl = this.injector.get(NgControl, null);

    console.log(`[FormDateRange] 元件初始化:`, {
      name: this.name,
      dateType: this.dateType,
      inputType: this.inputType,
      hasNgControl: !!this.ngControl,
      hasMinDate: !!this.minDate,
      hasMaxDate: !!this.maxDate
    });

    // 重要：不在初始化時設定預設的「今天+7天」限制
    //
    // 日期限制的優先順序：
    // 1. 外部傳入的 minDate（最高優先）
    // 2. Field Relationship Service 動態設定的 minStartDate（次優先）
    // 3. 都沒有則不限制
    //
    // 這樣的設計允許：
    // - 外部可以明確指定日期範圍（透過 minDate/maxDate）
    // - Field Relationship Service 可以根據其他欄位動態設定日期限制
    // - 沒有任何限制時，使用者可以自由選擇日期

    console.log('[FormDateRange] 不設定強制的預設日期限制，保持彈性');
  }

  ngAfterViewInit(): void {
    // 延遲驗證 DOM 約束設定
    Promise.resolve().then(() => {
      this.verifyDOMConstraint();
    });
  }

  ngOnDestroy(): void {
    // 清理
  }

  /**
   * 格式化日期以符合 input 類型要求
   * @param dateValue 日期值（可能是 YYYY-MM-DD 或 YYYY-MM-DDTHH:mm 格式）
   * @returns 符合當前 inputType 的日期格式
   */
  private formatDateForInputType(dateValue: string | undefined): string | undefined {
    if (!dateValue) {
      return undefined;
    }

    try {
      // 檢查日期是否有效
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        console.warn('[FormDateRange] 無效的日期值:', dateValue);
        return undefined;
      }

      // 判斷目標格式：如果 dateType 是 'datetime'，使用 datetime-local 格式，否則使用 date 格式
      const targetType = this.dateType === 'datetime' ? 'datetime-local' : 'date';

      if (targetType === 'datetime-local') {
        // 需要 YYYY-MM-DDTHH:mm 格式（使用本地時間，避免時區問題）
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const formatted = `${year}-${month}-${day}T${hours}:${minutes}`;
        console.log('[FormDateRange] 格式化為 datetime-local:', dateValue, '->', formatted);
        return formatted;
      } else {
        // type="date" 需要 YYYY-MM-DD 格式（使用本地時間，避免時區問題）
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formatted = `${year}-${month}-${day}`;
        console.log('[FormDateRange] 格式化為 date:', dateValue, '->', formatted);
        return formatted;
      }
    } catch (error) {
      console.error('[FormDateRange] 格式化日期錯誤:', error);
      return undefined;
    }
  }

  /**
   * 格式化日期約束以符合 input 類型要求
   */
  private formatDateConstraint(dateValue: string): string {
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        console.warn(`[FormDateRange] 無效的日期值: ${dateValue}`);
        return '';
      }

      // 根據 dateType 決定日期格式
      if (this.dateType === 'datetime') {
        return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm 格式
      } else {
        return date.toISOString().slice(0, 10); // YYYY-MM-DD 格式
      }
    } catch (error) {
      console.error(`[FormDateRange] 格式化日期錯誤:`, error);
      return '';
    }
  }

  private verifyDOMConstraint(): void {
    // 瀏覽器會自動處理 min 和 max 屬性，這個方法僅用於 debug
    console.log(`[FormDateRange] 日期限制:`, {
      minDate: this.minDate,
      maxDate: this.maxDate,
      minEndDate: this.minEndDate,
      dateType: this.dateType
    });
  }

  get inputType(): string {
    return this.dateType === 'datetime' ? 'datetime-local' : 'date';
  }

  // ControlValueAccessor 實作
  writeValue(value: DateRangeValue | null): void {
    if (value) {
      this.value = { ...value };
      this.startDateValue = value.startDate || '';
      this.endDateValue = value.endDate || '';
    } else {
      this.value = { startDate: '', endDate: '' };
      this.startDateValue = '';
      this.endDateValue = '';
    }
    this.validateDateRange();
  }

  registerOnChange(fn: (value: DateRangeValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // 如有需要，可以處理 disabled 狀態
  }

  // 開始日期變更處理
  onStartDateChange(value: string): void {
    this.startDateValue = value;
    this.value.startDate = value;
    this.validateDateRange();
    this.emitValue();
  }

  // 結束日期變更處理
  onEndDateChange(value: string): void {
    this.endDateValue = value;
    this.value.endDate = value;
    this.validateDateRange();
    this.emitValue();
  }

  // 發送值變更
  private emitValue(): void {
    const hasValue = this.value.startDate || this.value.endDate;
    this.onChange(hasValue ? { ...this.value } : null);
  }

  // 日期範圍驗證
  private validateDateRange(): void {
    this.errorMessage = '';

    if (!this.value.startDate || !this.value.endDate) {
      // 如果任一日期為空，觸發驗證更新後返回
      this.triggerValidationUpdate();
      return; // 如果任一日期為空，不進行驗證
    }

    const startDate = new Date(this.value.startDate);
    const endDate = new Date(this.value.endDate);

    // 檢查日期是否有效
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      this.triggerValidationUpdate();
      return;
    }

    // 檢查開始日期是否早於最小日期限制
    if (this.minDate) {
      const minDateObj = new Date(this.minDate);
      if (!isNaN(minDateObj.getTime()) && startDate < minDateObj) {
        this.errorMessage = 'form.validation.start_date_before_min';
        console.warn('[FormDateRange] ❌ 開始日期早於最小日期限制:', {
          startDate: this.value.startDate,
          minDate: this.minDate
        });
        this.triggerValidationUpdate();
        return;
      }
    }

    // 檢查開始日期是否晚於最大日期限制
    if (this.maxDate) {
      const maxDateObj = new Date(this.maxDate);
      if (!isNaN(maxDateObj.getTime()) && startDate > maxDateObj) {
        this.errorMessage = 'form.validation.start_date_after_max';
        console.warn('[FormDateRange] ❌ 開始日期晚於最大日期限制:', {
          startDate: this.value.startDate,
          maxDate: this.maxDate
        });
        this.triggerValidationUpdate();
        return;
      }
    }

    // 檢查結束日期是否早於開始日期
    if (endDate < startDate) {
      this.errorMessage = 'form.validation.end_date_before_start';
      this.triggerValidationUpdate();
      return;
    }

    // 檢查是否允許相同日期
    if (!this.allowSameDate && endDate.getTime() === startDate.getTime()) {
      this.errorMessage = 'form.validation.same_date_not_allowed';
      this.triggerValidationUpdate();
      return;
    }

    // 驗證通過，觸發更新
    this.triggerValidationUpdate();
  }

  // 觸發驗證狀態更新
  private triggerValidationUpdate(): void {
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }
    // 觸發變更檢測以更新 UI
    this.cdr.markForCheck();
  }

  // 實作 Validator 介面的 validate 方法
  validate(control: AbstractControl): ValidationErrors | null {
    // 如果控制項值為空，不進行驗證
    if (!control.value) {
      return null;
    }

    const value = control.value;

    // 如果任一日期為空，不進行驗證
    if (!value.startDate || !value.endDate) {
      return null;
    }

    const startDate = new Date(value.startDate);
    const endDate = new Date(value.endDate);

    // 檢查日期是否有效
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return { 'dateRangeInvalidFormat': true };
    }

    // 檢查開始日期是否早於最小日期限制
    if (this.minDate) {
      const minDateObj = new Date(this.minDate);
      if (!isNaN(minDateObj.getTime()) && startDate < minDateObj) {
        return { 'dateRangeMinDate': true };
      }
    }

    // 檢查開始日期是否晚於最大日期限制
    if (this.maxDate) {
      const maxDateObj = new Date(this.maxDate);
      if (!isNaN(maxDateObj.getTime()) && startDate > maxDateObj) {
        return { 'dateRangeMaxDate': true };
      }
    }

    // 檢查結束日期是否早於開始日期
    if (endDate < startDate) {
      return { 'dateRangeInvalid': true };
    }

    // 檢查是否允許相同日期
    if (!this.allowSameDate && endDate.getTime() === startDate.getTime()) {
      return { 'dateRangeSameDate': true };
    }

    // 所有驗證通過
    return null;
  }

  // 格式化日期顯示
  formatDateForDisplay(dateValue: string | undefined): string {
    if (!dateValue) return '-';

    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue;

      if (this.dateType === 'datetime') {
        return date.toLocaleDateString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        return date.toLocaleDateString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
    } catch {
      return dateValue || '-';
    }
  }

  // 靜態驗證方法，可供表單驗證器使用
  static dateRangeValidator(allowSameDate: boolean = true) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as DateRangeValue;

      if (!value || !value.startDate || !value.endDate) {
        return null; // 如果沒有值，讓 required 驗證器處理
      }

      const startDate = new Date(value.startDate);
      const endDate = new Date(value.endDate);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { invalidDate: true };
      }

      if (endDate < startDate) {
        return { endDateBeforeStart: true };
      }

      if (!allowSameDate && endDate.getTime() === startDate.getTime()) {
        return { sameDateNotAllowed: true };
      }

      return null;
    };
  }
}
